import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ArrowLeftIcon } from '../../../../assets/icons/arrow-left.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/icons/delete-icon.svg';
import { ReactComponent as EditIcon } from '../../../../assets/icons/edit-pencil.svg';
import DefalutDashboard from '../../../../assets/images/dashboard/defalut-dashboard.png';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchCustomDashboards,
  updateCustomDashboard,
} from '../../../../redux/slices/CustomDashboardSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../../shared-components/button/Button';
import CustomCheckbox from '../../../../shared-components/custom-check-box/CustomCheckBox';
import CustomInputSwitch from '../../../../shared-components/custom-input-switch/CustomInputSwitch';
import DeleteWarning from '../../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import dashboardApi from '../../../../utils/api/custom-dashboard';

/**
 * Customize Dashboard view matching the provided UI screenshot.
 * @returns {any} The rendered component.
 */
function CustomizeDashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { dashboards, defaultDashboard, loading } = useAppSelector(
    (state) => state.customDashboard,
  );

  /**
   * Sets a specific layout as the default dashboard.
   * @param {string} id - The ID of the layout.
   * @param {boolean} value - Whether the layout is set as default.
   */
  const handleSetDefault = (id: string, value: boolean) => {
    dispatch(updateCustomDashboard({ id, data: { isDefault: value } }));
  };

  /**
   * Toggles the "Show as a Tab" property for a layout.
   * @param {string} id - The ID of the layout.
   * @param {boolean} value - Whether the layout should be shown as a tab.
   */
  const handleToggleTab = (id: string, value: boolean) => {
    dispatch(updateCustomDashboard({ id, data: { showAsTab: value } }));
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const [selectedLayout, setSelectedLayout] = React.useState<{ id: string; name: string } | null>(
    null,
  );

  /**
   * Opens the delete confirmation modal.
   * @param {any} id - The ID of the dashboard to delete.
   * @param {any} name - The name of the dashboard.
   */
  const handleDeleteClick = (id: string, name: string) => {
    setSelectedLayout({ id, name });
    setIsDeleteModalOpen(true);
  };

  /**
   * Handles the actual deletion after confirmation.
   */
  const handleConfirmDelete = async () => {
    if (selectedLayout) {
      try {
        await dashboardApi.deleteCustomDashboard(selectedLayout.id);
        setIsDeleteModalOpen(false);
        dispatch(fetchCustomDashboards({}));
      } catch (error) {
        console.error('Error deleting dashboard:', error);
      }
    }
  };

  useEffect(() => {
    dispatch(fetchCustomDashboards({}));
  }, []);

  const allLayouts = defaultDashboard ? [defaultDashboard, ...dashboards] : [...dashboards];

  return (
    <div className="customize-dashboard-wrap">
      <div className="customize-header">
        <div className="header-left">
          <ArrowLeftIcon className="back-arrow" onClick={() => navigate('/ev-admin/dashboard')} />
          <div className="customize-title">
            <span>Customize</span>
            <span>Dashboard</span>
          </div>
        </div>
        <div className="header-right">
          <MyButton
            label="Create Dashboard"
            buttonType="submit"
            onClickFunc={() => navigate('/ev-admin/create-custom-dashboard')}
            disableButton={!permissionCheck('cpoCustomizeDashboardAdd')}
          />
        </div>
      </div>

      <div className="customize-description">
        <div className="description-content">
          <h2>Customize Your Dashboard</h2>
          <p>
            Easily design your ideal dashboard by arranging widgets to match your workflow. Make
            real-time changes, save your layout, and switch between views anytime using the dropdown
            menu.
          </p>
        </div>
      </div>
      <div className="layout-cards-grid">
        {loading ? (
          <BatteryLoader />
        ) : (
          allLayouts.map((layout) => (
            <div key={layout.id} className={`layout-card ${layout.isDefault ? 'active' : ''}`}>
              <div className="card-preview">
                {layout.signedUrl ? (
                  <img
                    src={layout?.signedUrl || DefalutDashboard}
                    alt={layout.name}
                    className="dashboard-preview-img"
                  />
                ) : (
                  <div className="preview-skeleton">
                    <div className="skeleton-row header" />
                    <div className="skeleton-row content" />
                    <div className="skeleton-row content sub" />
                  </div>
                )}
              </div>

              <div className="card-info">
                <div className="info-top">
                  <span className="layout-name">{layout.name}</span>
                  <span className="updated-at">{layout.updatedAt}</span>
                </div>

                <div className="info-bottom">
                  <div className="default-toggle">
                    <CustomInputSwitch
                      name={`default-${layout.id}`}
                      inputLabel="Set as default"
                      value={layout.isDefault}
                      onChangeValue={(_, value) => handleSetDefault(layout.id, value)}
                      fontSize="small"
                    />
                  </div>

                  <div className="action-icons">
                    {layout.id !== 'default' && (
                      <>
                        <EditIcon
                          className="icon-btn edit"
                          onClick={() => navigate(`/ev-admin/edit-custom-dashboard/${layout.id}`)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event: React.KeyboardEvent<SVGSVGElement>) =>
                            event.key === 'Enter' &&
                            navigate(`/ev-admin/edit-custom-dashboard/${layout.id}`)
                          }
                        />
                        <DeleteIcon
                          className="icon-btn delete"
                          onClick={() => handleDeleteClick(layout.id, layout.name)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event: React.KeyboardEvent<SVGSVGElement>) =>
                            event.key === 'Enter' && handleDeleteClick(layout.id, layout.name)
                          }
                        />
                      </>
                    )}
                  </div>
                </div>

                {layout.id !== 'default' && (
                  <div className="tab-selection">
                    <CustomCheckbox
                      name={`tab-${layout.id}`}
                      checkboxLabel="Show as a Tab in Main Dashboard"
                      value={layout?.showAsTab || false}
                      onChangeValue={(_, value) => handleToggleTab(layout.id, value)}
                    />
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <ModalBox
        open={isDeleteModalOpen}
        closeModal={() => setIsDeleteModalOpen(false)}
        title="Delete Dashboard"
        header={false}
        rightCorner=""
        content={
          <DeleteWarning
            selectedRowId={selectedLayout?.id}
            title="Delete Dashboard"
            description={`Are you sure you want to delete "${selectedLayout?.name || 'this dashboard'}"?`}
            cancelButtonOnClick={() => setIsDeleteModalOpen(false)}
            submitButtonOnClick={handleConfirmDelete}
            submitSvgIcon={<DeleteIcon />}
          />
        }
      />
    </div>
  );
}

export default CustomizeDashboard;
